import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  Renderer2,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { Subject, takeUntil } from 'rxjs';
import { CheckoutService } from '../../services/checkout.service';
import { PAYMENTS_CONFIG } from '../../payments.config';
import { PaymentConfig, PaymentResult } from '../../models/payment.models';
import { CardNumberFormatDirective } from '../../directives/card-number-format.directive';
import { ExpiryFormatDirective } from '../../directives/expiry-format.directive';
import { NumericOnlyDirective } from '../../directives/numeric-only.directive';
import { expiryDateValidator } from '../../validators/expiry-date.validator';

/**
 * Visa & Mastercard payments through the iVeri 3-D Secure gateway.
 * The card details are captured with an ng-zorro form; the 3-D Secure step
 * runs inside an html-driven `nz-modal` hosting the gateway iframe.
 */
@Component({
  selector: 'lib-mastercard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzModalModule,
    NzSpinModule,
    CardNumberFormatDirective,
    ExpiryFormatDirective,
    NumericOnlyDirective,
  ],
  templateUrl: './mastercard.html',
  styleUrl: './mastercard.css',
})
export class Mastercard implements AfterViewInit, OnDestroy {
  readonly payloadConfig = input.required<PaymentConfig>();
  /**
   * Compact, labelled layout used inside the checkout dialog — the card fields
   * stack in a tinted panel and the terms/fee notes are dropped.
   */
  readonly compact = input(false);
  /** Emitted when the payment finally resolves (success or failure). */
  readonly paymentSubmitted = output<PaymentResult>();
  /** Emitted once the 3-D Secure modal opens, so the host can react. */
  readonly secureFlowStarted = output<void>();

  private readonly fb = inject(FormBuilder);
  private readonly checkoutService = inject(CheckoutService);
  private readonly config = inject(PAYMENTS_CONFIG);
  private readonly renderer = inject(Renderer2);
  private readonly message = inject(NzMessageService);

  private readonly destroy$ = new Subject<void>();
  private iframeLoadHandler: (() => void) | null = null;
  private iframeMessageHandler: ((e: MessageEvent) => void) | null = null;

  readonly cardType = signal<string | null>(null);
  readonly referenceNumber = signal('');
  readonly isProcessing = signal(false);
  readonly tempLoading = signal(false);
  readonly show3dSecure = signal(false);
  private paymentLock = false;

  user: any;

  paymentForm: FormGroup;

  readonly cvvInput = viewChild<ElementRef>('cvvInput');
  readonly expiryDateInput = viewChild<ElementRef>('expiryDateInput');
  readonly secureIframe = viewChild<ElementRef<HTMLIFrameElement>>('secureIframe');
  readonly secureFormRef = viewChild<ElementRef<HTMLFormElement>>('secureForm');

  constructor() {
    const rawUser = localStorage.getItem(this.config.userStorageKey ?? 'auth_user');
    this.user = rawUser ? JSON.parse(rawUser) : null;

    this.paymentForm = this.fb.group({
      cardNumber: [
        '',
        {
          validators: [
            Validators.required,
            Validators.pattern(
              '^(4[0-9]{3} ?[0-9]{4} ?[0-9]{4} ?[0-9]{4}|5[1-5][0-9]{2} ?[0-9]{4} ?[0-9]{4} ?[0-9]{4}|2(2[2-9]|[3-7][0-9]) ?[0-9]{4} ?[0-9]{4} ?[0-9]{4})$'
            ),
          ],
          updateOn: 'blur',
        },
      ],
      expiryDate: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(5),
          Validators.pattern('(0[1-9]|1[0-2])\\/[0-9]{2}'),
          expiryDateValidator,
        ],
      ],
      cvv: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(4),
          Validators.pattern('^[0-9]{3,4}$'),
        ],
      ],
    });
  }

  ngAfterViewInit(): void {
    // Auto-advance focus as each field becomes valid.
    this.paymentForm
      .get('cardNumber')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.focusNext('cardNumber', this.expiryDateInput()));

    this.paymentForm
      .get('expiryDate')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.focusNext('expiryDate', this.cvvInput()));

    this.iframeMessageHandler = (event) => this.handleIframeMessage(event);
    window.addEventListener('message', this.iframeMessageHandler);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.detachIframeLoadListener();
    if (this.iframeMessageHandler) {
      window.removeEventListener('message', this.iframeMessageHandler);
      this.iframeMessageHandler = null;
    }
  }

  get validExpiryDate(): string {
    const value = this.paymentForm.get('expiryDate')?.value ?? '';
    const [month, year] = value.split('/');
    return `${month}20${year}`;
  }

  setCardType(evt: { cardType: string | null; value: string }): void {
    this.cardType.set(evt.cardType);
    this.paymentForm.get('cardNumber')?.setValue(evt.value);
    this.paymentForm.updateValueAndValidity();
  }

  private focusNext(controlName: string, nextField?: ElementRef | null): void {
    if (this.paymentForm.get(controlName)?.valid) {
      nextField?.nativeElement?.focus();
    }
  }

  onPay(feeId: string | null = null, amount = 0): void {
    if (this.paymentLock || this.isProcessing()) return;

    if (!this.user) {
      this.message.error('User session not found. Please log in again.');
      return;
    }

    this.paymentLock = true;
    this.isProcessing.set(true);

    const payload: any = {
      userId: this.user.id,
      feeId,
      callbackUrl: this.payloadConfig().isWallet
        ? this.checkoutService.frontendUrl + 'wallet'
        : this.checkoutService.frontendUrl,
      paymentChannel: this.payloadConfig().paymentChannel,
      pan: this.paymentForm.get('cardNumber')?.value,
      expiryDate: this.validExpiryDate,
      cardSecurityCode: this.paymentForm.get('cvv')?.value,
    };
    if (amount > 0) payload.amount = amount;

    this.checkoutService
      .generateMerchantReferenceWithoutFee(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.referenceNumber.set(res.invoiceNumber);
          this.payloadConfig().MerchantReference = res.invoiceNumber;
          this.openSecureFlow();
        },
        error: () => {
          this.paymentLock = false;
          this.isProcessing.set(false);
          this.message.error('Failed to generate payment reference. Please try again.');
        },
      });
  }

  /** Opens the 3-D Secure modal, then submits the hidden form into its iframe. */
  private openSecureFlow(): void {
    const amount = this.payloadConfig()?.Amount ?? 0;
    if (!amount || amount < 1) {
      this.paymentLock = false;
      this.isProcessing.set(false);
      this.message.error('Amount should be greater than 0');
      return;
    }

    this.show3dSecure.set(true);
    this.tempLoading.set(true);
    this.secureFlowStarted.emit();

    // Wait for the modal (and its iframe) to render before posting the form.
    setTimeout(() => this.submitForm(), 0);
  }

  private submitForm(): void {
    const form = this.secureFormRef()?.nativeElement;
    if (!form) {
      this.message.error('Payment form is unavailable. Please refresh and try again.');
      return;
    }

    const merchantRef = this.payloadConfig()?.MerchantReference || this.referenceNumber();
    this.setHiddenInputValue(form, 'MerchantReference', merchantRef);

    const amountInMinorUnits = Math.round((this.payloadConfig().Amount ?? 0) * 100).toString();
    this.setHiddenInputValue(form, 'Amount', amountInMinorUnits);

    form.target = '3dsecureIframe';
    form.submit();

    this.attachIframeLoadListener();
  }

  private attachIframeLoadListener(): void {
    this.detachIframeLoadListener();
    const iframe = this.secureIframe()?.nativeElement;
    if (!iframe) return;

    this.iframeLoadHandler = () => {
      this.tempLoading.set(false);
      this.isProcessing.set(false);
      this.paymentLock = false;

      try {
        iframe.contentWindow?.postMessage(
          { type: 'PAYMENT_COMPLETE', data: this.payloadConfig() },
          this.config.paymentRedirectUrl
        );
      } catch (err) {
        console.warn('[Mastercard] postMessage failed:', err);
      }

      this.detachIframeLoadListener();
    };

    iframe.addEventListener('load', this.iframeLoadHandler);
  }

  private detachIframeLoadListener(): void {
    if (this.iframeLoadHandler) {
      this.secureIframe()?.nativeElement?.removeEventListener('load', this.iframeLoadHandler);
      this.iframeLoadHandler = null;
    }
  }

  private handleIframeMessage(event: MessageEvent): void {
    if (event.origin !== this.config.iveriGateway) return;

    const { type, data } = event.data ?? {};
    switch (type) {
      case 'PAYMENT_SUCCESS':
        this.show3dSecure.set(false);
        this.paymentSubmitted.emit({ success: true, data });
        break;
      case 'PAYMENT_FAILED':
        this.message.error('Payment failed. Please check your card details and try again.');
        this.show3dSecure.set(false);
        this.paymentSubmitted.emit({ success: false, data });
        break;
      case 'PAYMENT_CANCELLED':
        this.show3dSecure.set(false);
        break;
      default:
        break;
    }
  }

  private setHiddenInputValue(form: HTMLFormElement, name: string, value: string): void {
    const input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    if (input) {
      this.renderer.setProperty(input, 'value', value);
    } else {
      const newInput = this.renderer.createElement('input') as HTMLInputElement;
      this.renderer.setAttribute(newInput, 'type', 'hidden');
      this.renderer.setAttribute(newInput, 'name', name);
      this.renderer.setProperty(newInput, 'value', value);
      this.renderer.appendChild(form, newInput);
    }
  }
}
